import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFeedStore, type SearchFilterPatch } from "@/app/store/feedStore";
import { useAuthStore } from "@/app/store/authStore";
import { useProcessFeeds } from "@/shared/hooks/useProcessFeeds";
import {
  RssIcon,
  BookOpenIcon,
  StarIcon,
  ClockIcon,
  TrendingUpIcon,
  RefreshCwIcon,
  AlertTriangleIcon,
  ZapIcon,
  ShieldIcon,
  SparklesIcon,
  ClipboardCheckIcon,
  GaugeIcon,
  type LucideIcon,
} from "lucide-react";
import { format } from "date-fns";
import { todayIso } from "@/shared/utils/today";
import toast from "react-hot-toast";
import {
  ComplexityLevel,
  ImpactLevel,
  type DashboardSummary,
} from "@/shared/types/api";
import { ApiClient } from "@/shared/services/api";
import { SegmentBadge } from "@/shared/components/classification/SegmentBadge";
import { EmptyState } from "@/shared/components/EmptyState";
import { ExpandableText } from "@/shared/components/ExpandableText";
import { OverflowList } from "@/shared/components/OverflowList";
import { ItemTagChip } from "@/shared/components/ItemTagChip";
import { cn } from "@/shared/utils/cn";

/** The Recent updates table's length — the only reason the dashboard loads feed items. */
const RECENT_ITEMS_COUNT = 10;

type Accent = "brand" | "warning" | "success" | "danger" | "info";

const accentClass: Record<Accent, string> = {
  brand: "bg-primary/10 text-primary",
  warning: "bg-warning-subtle text-warning-foreground",
  success: "bg-success-subtle text-success-foreground",
  danger: "bg-danger-subtle text-danger-foreground",
  info: "bg-info-subtle text-info-foreground",
};

/**
 * A dashboard card describes the whole filter state, not a delta — landing on the feed list with
 * a leftover filter from an earlier visit would show a count that does not match the card.
 */
const CLEARED_FILTERS: SearchFilterPatch = {
  searchTerm: "",
  selectedProductType: "",
  showUnreadOnly: false,
  showStarredOnly: false,
  selectedSegments: [],
  selectedTags: [],
  selectedClientIds: [],
  dateFrom: "",
  dateTo: "",
  selectedComplexity: "",
  selectedImpact: "",
  // SC-423: a tile's count has no assignment filter, so the list it opens must not either.
  selectedAssigneeIds: [],
  selectedReviewStatus: "",
  // SC-421: the summary counts non-deleted items of every release type and version count, so the
  // feed's Release Type, Multi-version and Deleted filters must not survive the click either.
  selectedReleaseType: "",
  showMultiVersion: false,
  showDeleted: false,
};

/**
 * SC-241: the informational row first, the actionable row second.
 * `actionable` selects cards by whether they carry a filter, so a card cannot claim to be
 * actionable without something to actually do.
 */
const STAT_ROWS: { key: string; label: string; actionable: boolean }[] = [
  { key: "informational", label: "Overview", actionable: false },
  { key: "actionable", label: "Jump into the feed", actionable: true },
];

export const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const {
    items,
    isLoading,
    error,
    loadFeedItems,
    loadProductTypes,
    markItemAsStarred,
    setSearchFilters,
    clearError,
  } = useFeedStore();
  const navigate = useNavigate();
  const { processFeeds, isProcessing } = useProcessFeeds();

  // SC-421: every card number comes from the server-side summary, counted over the whole corpus
  // with the same filter the card applies on click. The cards used to be derived from the first
  // 100 loaded items, so they stopped matching the feed list as soon as there were more.
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // The local calendar day, used both for the Today card's filter and for the summary's count of
  // it, so the two cannot disagree around midnight.
  const today = todayIso();

  useEffect(() => {
    const initializeDashboard = async () => {
      setSummaryLoading(true);
      const loadSummary = async () => {
        try {
          setSummary((await ApiClient.getDashboard(today)).summary);
        } catch {
          setSummary(null);
        } finally {
          setSummaryLoading(false);
        }
      };
      const loadRecentItems = async () => {
        await loadProductTypes();
        // Only the Recent updates table needs items now, and it shows ten.
        await loadFeedItems({ pageSize: RECENT_ITEMS_COUNT });
      };
      await Promise.all([loadRecentItems(), loadSummary()]);
    };

    initializeDashboard();
  }, [user, loadFeedItems, loadProductTypes, today]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  const recentItems = items.slice(0, RECENT_ITEMS_COUNT);

  const handleMarkAsStarred = async (itemId: number, isStarred: boolean) => {
    if (!user) return;

    const success = await markItemAsStarred(itemId, user.userId, isStarred);
    if (success) {
      // Keep the Starred card in step with a star toggled from the table below it.
      setSummary((s) =>
        s ? { ...s, starredItems: Math.max(0, s.starredItems + (isStarred ? 1 : -1)) } : s,
      );
      toast.success(`${isStarred ? "Starred" : "Unstarred"} item`);
    }
  };

  const handleCardClick = (filter: SearchFilterPatch) => {
    setSearchFilters(filter);
    navigate("/feeds");
  };

  // Without a summary (the request failed) a card shows a dash rather than a misleading 0.
  const count = (pick: (s: DashboardSummary) => number): number | string =>
    summary ? pick(summary) : "—";

  const classifiedPct =
    summary && summary.totalItems > 0
      ? Math.round((summary.classifiedItems / summary.totalItems) * 100)
      : 0;

  /**
   * SC-234: a card with a `filter` is actionable — clicking it applies that filter to the feed
   * list and navigates there, so the number on the card is reproducible. Cards without one are
   * purely informational (there is no feed filter that would reproduce them).
   */
  const statCards: {
    label: string;
    value: number | string;
    hint?: string;
    icon: LucideIcon;
    accent: Accent;
    filter?: SearchFilterPatch;
  }[] = [
    {
      // SC-241 names Total Items as purely informational, which supersedes SC-234's blanket
      // "make the cards navigable" - there is no filter that reproduces "all items" as a
      // meaningful narrowing anyway.
      label: "Total Items",
      value: count((s) => s.totalItems),
      icon: RssIcon,
      accent: "brand",
    },
    {
      label: "Unread",
      value: count((s) => s.unreadItems),
      icon: BookOpenIcon,
      accent: "warning",
      filter: { ...CLEARED_FILTERS, showUnreadOnly: true },
    },
    {
      label: "Starred",
      value: count((s) => s.starredItems),
      icon: StarIcon,
      accent: "warning",
      filter: { ...CLEARED_FILTERS, showStarredOnly: true },
    },
    {
      label: "Today",
      value: count((s) => s.itemsToday),
      icon: TrendingUpIcon,
      accent: "success",
      filter: { ...CLEARED_FILTERS, dateFrom: today, dateTo: today },
    },
    {
      // SC-421: counts exactly what its filter shows - Critical complexity only.
      label: "Critical",
      value: count((s) => s.criticalItems),
      hint: "Complexity: Critical",
      icon: AlertTriangleIcon,
      accent: "danger",
      filter: { ...CLEARED_FILTERS, selectedComplexity: ComplexityLevel.Critical },
    },
    {
      // SC-421: counts exactly what its filter shows - High impact only.
      label: "High Impact",
      value: count((s) => s.highImpactItems),
      hint: "Impact: High",
      icon: ZapIcon,
      accent: "info",
      filter: { ...CLEARED_FILTERS, selectedImpact: ImpactLevel.High },
    },
    {
      label: "Classified",
      value: count((s) => s.classifiedItems),
      hint: summary ? `${classifiedPct}% of total` : undefined,
      icon: ShieldIcon,
      accent: "brand",
    },
    ...(summary
      ? [
          {
            label: "Auto-Classified",
            value: summary.autoClassifiedItems,
            hint: "By the classifier",
            icon: SparklesIcon,
            accent: "info" as Accent,
          },
          {
            label: "Needs Review",
            value: summary.itemsNeedingReview,
            hint: "Low confidence, unclassified",
            icon: ClipboardCheckIcon,
            accent: "warning" as Accent,
          },
          {
            label: "Avg Confidence",
            value: `${Math.round(summary.averageClassificationConfidence * 100)}%`,
            hint: "Auto-classified only",
            icon: GaugeIcon,
            accent: "success" as Accent,
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Dashboard
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Welcome back — here&apos;s your feed overview.
          </p>
        </div>
        {user?.isAdmin && (
          <button
            onClick={processFeeds}
            disabled={isProcessing}
            className="btn-primary gap-2"
          >
            <RefreshCwIcon
              className={cn("h-4 w-4", isProcessing && "animate-spin")}
            />
            <span>{isProcessing ? "Processing…" : "Process Feeds"}</span>
          </button>
        )}
      </div>

      {/* Stats — SC-241: informational cards on the first row, actionable ones on the second,
          each row styled differently so which is which reads without clicking. A card is
          actionable exactly when it carries a filter that reproduces its number. */}
      {isLoading || summaryLoading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="stat-card">
              <div className="skeleton h-8 w-8 rounded-lg" />
              <div className="skeleton mt-3 h-7 w-12" />
              <div className="skeleton mt-1 h-3 w-16" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {STAT_ROWS.map((rowSpec) => {
            const cards = statCards.filter((c) => !!c.filter === rowSpec.actionable);
            if (cards.length === 0) return null;

            return (
              <section key={rowSpec.key} aria-label={rowSpec.label}>
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {rowSpec.label}
                </h2>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                  {cards.map((s) => {
              const Icon = s.icon;
              const body = (
                <>
                  <span
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-lg",
                      accentClass[s.accent],
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <p className="mt-3 text-2xl font-bold tabular-nums text-foreground">
                    {s.value}
                  </p>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                  {s.hint && (
                    <p className="text-xs text-muted-foreground/80">{s.hint}</p>
                  )}
                </>
              );

                    // SC-234/SC-241: an actionable card filters the feed and is visibly
                    // interactive; an informational one is inert and deliberately flatter.
                    if (!s.filter) {
                      return (
                        <div key={s.label} className="stat-card bg-muted/40">
                          {body}
                        </div>
                      );
                    }

                    const filter = s.filter;
                    return (
                      <button
                        key={s.label}
                        type="button"
                        onClick={() => handleCardClick(filter)}
                        aria-label={`${s.label}: show these items in the feed`}
                        className="stat-card card-hover cursor-pointer border-primary/30 text-left focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        {body}
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Recent items */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-border p-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Recent updates
            </h3>
            <p className="text-sm text-muted-foreground">
              Latest classified items from your feeds
            </p>
          </div>
          <Link to="/feeds" className="btn-secondary btn-sm">
            View all
          </Link>
        </div>

        {isLoading ? (
          <div className="space-y-3 p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="skeleton mt-1.5 h-2 w-2 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="skeleton h-4 w-3/4" />
                  <div className="skeleton h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : recentItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-left">
              <thead>
                <tr className="bg-muted/50">
                  {/* SC-277: Complexity and Impact are deliberately absent - they stay on the
                      RSS Feeds view, where there is room for them. */}
                  {[
                    "Title & Feed",
                    "Tags",
                    "Segments",
                    "Published",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground"
                    >
                      {h}
                    </th>
                  ))}
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    <span className="sr-only">Starred</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recentItems.map((item) => (
                  <tr
                    key={item.id}
                    className={cn(
                      "transition-colors hover:bg-muted/50",
                      !item.isRead && "bg-primary/[0.04]",
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2.5">
                        <span
                          className={cn(
                            "mt-1.5 h-2 w-2 flex-shrink-0 rounded-full",
                            item.isRead
                              ? "bg-transparent ring-1 ring-border"
                              : "bg-primary",
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <h4
                            className={cn(
                              "text-sm",
                              item.isRead
                                ? "font-medium text-muted-foreground"
                                : "font-semibold text-foreground",
                            )}
                          >
                            {item.title}
                          </h4>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {item.feedName}
                          </p>
                          {item.description && (
                            <ExpandableText
                              text={item.description}
                              className="mt-1 text-sm text-muted-foreground"
                            />
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.tags && item.tags.length > 0 ? (
                        <OverflowList
                          items={item.tags}
                          maxVisible={2}
                          getKey={(tag) => tag}
                          renderItem={(tag) => <ItemTagChip tag={tag} />}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.businessSegments && item.businessSegments.length > 0 ? (
                        <OverflowList
                          items={item.businessSegments}
                          maxVisible={3}
                          getKey={(segment) => segment}
                          renderItem={(segment) => (
                            <SegmentBadge
                              segment={segment}
                              source={item.businessSegmentsSource}
                            />
                          )}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <ClockIcon className="h-3 w-3" />
                        {format(new Date(item.publishedDate), "MMM dd, HH:mm")}
                      </div>
                      {item.yearWeek && (
                        <div className="mt-0.5 text-xs text-muted-foreground/70">
                          {item.yearWeek}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() =>
                          handleMarkAsStarred(item.id, !item.isStarred)
                        }
                        aria-pressed={item.isStarred}
                        aria-label={
                          item.isStarred ? "Unstar item" : "Star item"
                        }
                        title={item.isStarred ? "Unstar" : "Star"}
                        className="btn-icon"
                      >
                        <StarIcon
                          className={cn(
                            "h-4 w-4",
                            item.isStarred && "fill-warning text-warning",
                          )}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={RssIcon}
            title="No feed items yet"
            description="Process the feeds to pull in the latest updates, or check back later."
          />
        )}
      </div>
    </div>
  );
};
